import React from 'react';
import { Box, Paper, TextField } from '@mui/material';

/**
 * A reusable split view component with optional filter, list panel on the left and detail panel on the right
 * @param {Object} props
 * @param {React.ReactNode} props.listPanel - Content for the left panel (typically a list)
 * @param {React.ReactNode} props.detailPanel - Content for the right panel (typically details)
 * @param {number} props.listWidth - Width of the list panel in pixels (default: 340)
 * @param {Object} props.listProps - Additional props for the list container
 * @param {Object} props.detailProps - Additional props for the detail container
 * @param {Object} props.containerProps - Additional props for the main container
 * @param {Object} props.filterProps - Properties for the filter TextField
 * @param {string} props.filterProps.value - Current filter value
 * @param {function} props.filterProps.onChange - Filter change handler
 * @param {string} props.filterProps.label - Filter field label
 * @param {Object} props.filterProps.InputProps - Additional props for the TextField input
 * @param {boolean} props.showFilter - Whether to show the filter field (default: false)
 */
const SplitView = ({ 
  listPanel, 
  detailPanel, 
  listWidth = 340, 
  listProps = {}, 
  detailProps = {},
  containerProps = {},
  showFilter = false,
  filterProps = {}
}) => {
  const defaultFilterProps = {
    size: 'small',
    sx: { 
      width: listWidth,
      bgcolor: '#232323',
      input: { color: '#fff' },
      label: { color: '#bbb' }
    },
    InputLabelProps: { style: { color: '#bbb' } },
    InputProps: {
      ...filterProps?.InputProps,
      endAdornment: filterProps?.endAdornment || filterProps?.InputProps?.endAdornment
    }
  };

  const scrollbarStyle = {
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#252526',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255, 255, 255, 0.15)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: 'rgba(255, 255, 255, 0.25)',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255, 255, 255, 0.15) #252526',
  };

  const commonPaperStyle = {
    height: '100%',
    color: '#fff',
    border: '1px solid #444',
    mb: 0,
    overflow: 'auto',
    p: 0,
    borderRadius: 0,
    boxShadow: 'none',
    display: 'flex',
    flexDirection: 'column',
    ...scrollbarStyle,
  };

  return (
    <Box 
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: '100%',
        height: '100%',
        minHeight: 0,
        maxHeight: '100%',
        boxSizing: 'border-box',
        alignItems: 'stretch',
        ...containerProps?.sx
      }}
      {...containerProps}
    >
      {showFilter && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: 'min-content' }}>
          <TextField
            {...defaultFilterProps}
            {...filterProps}
          />
        </Box>
      )}
      <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 1,
        flex: 1,
        minHeight: 0,
        alignItems: 'stretch'
      }}>
        <Paper 
          sx={{
            ...commonPaperStyle,
            flex: `0 0 ${listWidth}px`,
            maxWidth: listWidth + 40,
            minWidth: Math.max(220, listWidth - 120),
            bgcolor: '#181818',
            ...listProps?.sx
          }}
          {...listProps}
        >
          {listPanel}
        </Paper>

        <Paper 
          sx={{
            ...commonPaperStyle,
            flex: '1 1 0%',
            minWidth: 0,
            bgcolor: '#232323',
            ...detailProps?.sx
          }}
          {...detailProps}
        >
          {detailPanel}
        </Paper>
      </Box>
    </Box>
  );
};

export default SplitView;
